import React, { useCallback, useMemo } from 'react';
import { PropTypes } from 'prop-types';
import MiniForm from 'components/MiniForm';
import { useTranslation } from 'react-i18next';
import { updateInternalReference, useOrder } from 'hooks/useOrders';
import { STATUS_WHERE_USER_CAN_ADD_DOCUMENTS } from 'utils/order/constants';

const InternalReference = ({ value, shipmentId, orderId, isRecurring }) => {
  const { t } = useTranslation('OrdersOrderDetail');
  const { order, mutate } = useOrder(orderId, isRecurring);

  const orderStatus = order.status;

  const canEditInternalReference = useMemo(
    () => STATUS_WHERE_USER_CAN_ADD_DOCUMENTS.includes(orderStatus),
    [orderStatus]
  );

  const handleSubmit = useCallback(
    async ({ value: internalReference }) => {
      // Internal references are defined at shipment and stop levels
      const shipments = order.shipments.map((shipment) => {
        if (Number(shipment.id) === Number(shipmentId)) {
          return { ...shipment, internal_reference: internalReference };
        }
        return shipment;
      });
      const stops = order.stops.map((stop) => {
        const shipmentIds = Object.keys(stop.shipments);
        if (shipmentIds.length === 1 && shipmentIds.includes(shipmentId)) {
          return { ...stop, internal_reference: internalReference };
        }
        return stop;
      });
      mutate({ data: { ...order, shipments, stops } }, false);
      await updateInternalReference({ internalReference, shipmentId });
      mutate();
    },
    [shipmentId, mutate, order]
  );

  return (
    <MiniForm
      value={value}
      submitValue={handleSubmit}
      id="internalReference"
      labelField={t('internal_reference_form_title')}
      help={
        <p className="help">
          <span>{t('internal_reference_form_help_A')}</span>
          <span className="italic">
            {t('internal_reference_form_italic_text')}
          </span>
          <span>{t('internal_reference_form_help_B')}</span>
        </p>
      }
      valueLabel={t('internal_reference_title')}
      toEditLabel={t('internal_reference_empty_placeholder')}
      submitLabel={t('internal_reference_form_submit_button_label')}
      cancelLabel={t('internal_reference_form_cancel_button_label')}
      editLabel={t('internal_reference_edit_hover_action_label')}
      disableEdit={!canEditInternalReference}
    />
  );
};

InternalReference.propTypes = {
  value: PropTypes.string.isRequired,
  shipmentId: PropTypes.string.isRequired,
  orderId: PropTypes.string.isRequired,
  isRecurring: PropTypes.bool,
};

InternalReference.defaultProps = {
  isRecurring: false,
};

export default InternalReference;
